import { Box } from "@mui/material";
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { SectionHeader, type SectionTab } from "../../components/SectionHeader.js";
import {
  HeaderActionContext,
  useHeaderActionTarget,
} from "../support/HeaderActionSlot.js";
import { AgentControlButton } from "./components/AgentControlButton.js";
import { AgentWorkingNow } from "./components/AgentWorkingNow.js";
import { SalesHealthPill } from "./components/SalesHealthPill.js";
import { SteerAgentButton } from "./components/SteerAgentButton.js";
import { devLog } from "./devLog.js";

/**
 * Six-tab native shell for the autonomous sales agent (Leads · Harvester ·
 * Campaigns · Strategy · Knowledge · Status). Title, tab nav, and the operator
 * controls all live in the shared `<SectionHeader>` so every dashboard section
 * shares one chrome. The always-visible `<AgentWorkingNow>` pills ride at the
 * far RIGHT of that tab (menu) row via `tabsRight` — on EVERY tab they name the
 * two AI agents (Lead Generator + Harvester) and show which one is working
 * right now (2026-06-20; moved into the menu row 2026-08-07, dropping the
 * "Your two AI agents" caption). The Steer + Pause/Resume controls sit in the header's right
 * cluster (a matched pair); sub-pages inject their primary action there via
 * `<HeaderAction>` (the empty `<Box>` mounted first).
 *
 *   Leads     — both bots' leads (the default landing) + a "Today" filter +
 *               the evidence drawer; each row badged with its source agent
 *   Harvester — the contact harvester's raw pool (route `contacts`); promoted
 *               orgs become Leads. Renamed from "Contacts" 2026-06-20 for
 *               clarity — the page + route are unchanged.
 *   Campaigns — the harvester's operator-defined targets
 *   Strategy  — agent's self-written monthly memo + version history
 *   Knowledge — what the agent can do / see / has learned
 *   Status    — is the agent working, the plan, the spend + lead quality, its
 *               standing directives, and (at the bottom) its live thinking
 *
 * Leads is first (and the index redirect target) so the operator's home is
 * the pipeline; the old Today tab was retired 2026-06-16.
 *
 * **The Brain tab was retired 2026-08-07**: its transcript is now the bottom
 * section of Status (`status/StatusPage.tsx` → `<BrainSection>`), and Status
 * moved to LAST so the strip runs "the work" → "the machine". `/sales/brain`
 * redirects to `/sales/status` for old bookmarks.
 */
const TABS: SectionTab[] = [
  { to: "leads",     label: "Leads" },
  { to: "contacts",  label: "Harvester" },
  { to: "campaigns", label: "Campaigns" },
  { to: "strategy",  label: "Strategy" },
  { to: "knowledge", label: "Knowledge" },
  { to: "status",    label: "Status" },
];

export function SalesLayout(): JSX.Element {
  const [slotEl, setSlotEl] = useHeaderActionTarget();
  const location = useLocation();

  // Dev-mode breadcrumb so an operator opening the page can confirm in
  // devtools that the layout actually mounted on the expected URL.
  // Production builds strip these calls.
  useEffect(() => {
    devLog("layout/mount", { path: location.pathname });
  }, [location.pathname]);

  return (
    <HeaderActionContext.Provider value={slotEl}>
      <>
        <SectionHeader
          title="Sales"
          tabs={TABS}
          tabsAriaLabel="Sales sections"
          right={
            <>
              <Box ref={setSlotEl} sx={{ display: "flex", alignItems: "center", gap: 1 }} />
              {/* At-a-glance backend health (polls /api/sales/health) sits left
                  of the operator controls — restored 2026-06-16 after it was
                  dropped in the SectionHeader unification. */}
              <SalesHealthPill />
              {/* The "which agent works" selector moved to the Status tab
                  (2026-06-21) — see status/StatusPage.tsx — so the header stays
                  a tight Health · Steer · Pause cluster. */}
              <SteerAgentButton />
              <AgentControlButton />
            </>
          }
          /* Always-visible "which AI agent is working now" — both agents named
             (Lead Generator + Harvester) with their live state, pinned to the
             far right of the menu row on every tab. */
          tabsRight={<AgentWorkingNow />}
        />
        <Outlet />
      </>
    </HeaderActionContext.Provider>
  );
}
